import { ComponentTypes } from '../../Data/Components';
import SelectAndConnect from '../../Data/SelectAndConnect';

const SUBMIT_BUTTON_TEXT_REPLACEMENT_REGEX = /\[submit_button_text\]/g;
const LEGAL_STATEMENT_REPLACEMENT_REGEX = /\[exclusive_supplier_name\]/g;

/**
 * For non US forms we need to update the privacy policy every
 * time a new country code is selected in the phone number input.
 */
export const getLocaleForPrivacyPolicy = (
  phoneNumberCountryCode,
  countryCode,
  languageCode,
  localeCode
) => {
  return countryCode === 'US'
    ? localeCode
    : `${languageCode}_${phoneNumberCountryCode}`;
};

export const isLegalStatementValid = ({
  statement,
  supplierId,
  singleSupplierStatement,
  multipleSupplierMatchStatement,
  components,
  currentPage,
  submitButtonText,
  customerMatches,
}) => {
  const statementShown = getLegalStatementToShow({
    statement,
    supplierId,
    singleSupplierStatement,
    multipleSupplierMatchStatement,
    components,
    currentPage,
    submitButtonText,
    customerMatches,
  });
  return typeof statementShown === 'string' && statementShown !== '';
};

export const shouldLegalStatementBeVisible = ({
  isDynamicHeightEnabled,
  hasSupplierMatchQuestion,
  shouldRenderLegalStatement,
  isConsentStatementAboveNavigation,
  hasStatementGraduallyDisappeared,
  isFinalPageBeforeSubmission,
  isPartialSubmissionPage,
  isSelectAndConnectPage,
}) => {
  /*
   * If dynamic height is enabled the dynamic height logic will determine
   * whether the legal statement should be visible or not. It then is the
   * <LegalStatement /> component's responsibility to then render that
   * statement depending on additional separate state value checks.
   */
  if (isDynamicHeightEnabled) {
    return (
      shouldRenderLegalStatement &&
      (!isConsentStatementAboveNavigation ||
        (isConsentStatementAboveNavigation &&
          !hasStatementGraduallyDisappeared))
    );
  }

  /*
   * If dynamic height is not enabled, we only need to check for the following conditions:
   */
  return Boolean(
    hasSupplierMatchQuestion ||
      isFinalPageBeforeSubmission ||
      isSelectAndConnectPage ||
      isPartialSubmissionPage
  );
};

export const currentPageHasSelectAndConnectComponents = (
  components,
  currentPage
) => {
  return Object.values(components).some(
    (component) =>
      component.component.page === currentPage &&
      component.component.type === ComponentTypes.SELECT_AND_CONNECT
  );
};

/**
 * formatSingleSupplierStatement
 *
 * Supplier legal statements will be supplied with a placeholder for the suppliers who have been matched to the customer. This function replaces the placeholder with the appropriate supplier name. If there is no supplier name or the statement has not yet been obtained it will return undefined.
 */
const formatSingleSupplierStatement = ({
  predefinedBrandLabel,
  singleSupplierStatement,
  submitButtonText,
}) => {
  if (typeof singleSupplierStatement !== 'string' || !predefinedBrandLabel) {
    return;
  }

  let statement = singleSupplierStatement.replace(
    LEGAL_STATEMENT_REPLACEMENT_REGEX,
    predefinedBrandLabel
  );

  statement = replaceSubmitButtonTextPlaceholder({
    statement,
    submitButtonText,
  });

  return statement;
};

/**
 * replaceSubmitButtonTextPlaceholder
 *
 * Replaces the submit button text placeholder in any legal statement
 */
const replaceSubmitButtonTextPlaceholder = ({
  statement,
  submitButtonText,
}) => {
  if (typeof statement !== 'string') {
    return;
  }

  return statement.replace(
    SUBMIT_BUTTON_TEXT_REPLACEMENT_REGEX,
    submitButtonText
  );
};

export const isPredefinedSupplierBrandAvailable = ({
  supplierId,
  customerMatches,
}) => {
  return (
    supplierId && Array.isArray(customerMatches) && customerMatches.length === 1
  );
};

/**
 * getLegalStatementToShow
 *
 * Decides whether the normal and supplier legal statement should be shown and formats the statement with suppliers if required.
 */
export const getLegalStatementToShow = ({
  components,
  supplierId,
  singleSupplierStatement,
  customerMatches,
  multipleSupplierMatchStatement,
  statement,
  currentPage,
  statementOverride,
  submitButtonText,
}) => {
  if (statementOverride) {
    return statementOverride;
  }

  if (currentPageHasSelectAndConnectComponents(components, currentPage)) {
    return replaceSubmitButtonTextPlaceholder({
      statement: multipleSupplierMatchStatement,
      submitButtonText: SelectAndConnect.SUBMIT_BUTTON_TEXT,
    });
  }

  if (isPredefinedSupplierBrandAvailable({ supplierId, customerMatches })) {
    const predefinedBrandLabel = customerMatches[0].brandLabel;
    return formatSingleSupplierStatement({
      predefinedBrandLabel,
      singleSupplierStatement,
      submitButtonText,
    });
  }

  return replaceSubmitButtonTextPlaceholder({
    statement,
    submitButtonText,
  });
};
